import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import {
    NumberFormatAmountScale,
    NumberFormatCurrencyPlacement,
    NumberFormatCurrencySymbol,
    NumberFormatNegativeFormat,
    NumberFormatZeroDisplay,
} from "@common/types/reports/number-format-settings";
import { currencyFormatterFinancialStatements } from "./currency";

export const toFiniteNumber = (
    value: string | number | null | undefined,
): number => {
    const parsed = typeof value === "number" ? value : parseFloat(value ?? "");
    return Number.isFinite(parsed) ? parsed : 0;
};

export const numberFormatterShort = new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
});

export const numberFormatter = new Intl.NumberFormat("en-US");

export const numberFormatterNoFractions = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
});

export const numberFormatterFinancialStatements =
    currencyFormatterFinancialStatements;

const currencySymbolText = (symbol: NumberFormatCurrencySymbol): string => {
    switch (symbol) {
        case NumberFormatCurrencySymbol.DOLLAR:
            return "$";
        case NumberFormatCurrencySymbol.US_DOLLAR:
            return "US$";
        case NumberFormatCurrencySymbol.USD:
            return "USD ";
        case NumberFormatCurrencySymbol.NONE:
        default:
            return "";
    }
};

const scaleAmount = (value: number, scale: NumberFormatAmountScale): number => {
    switch (scale) {
        case NumberFormatAmountScale.THOUSANDS:
            return value / 1000;
        case NumberFormatAmountScale.MILLIONS:
            return value / 1_000_000;
        case NumberFormatAmountScale.FULL:
        default:
            return value;
    }
};

const applyNegativeFormat = (
    magnitude: string,
    negativeFormat: NumberFormatNegativeFormat,
): string => {
    switch (negativeFormat) {
        case NumberFormatNegativeFormat.MINUS_LEFT:
            return `-${magnitude}`;
        case NumberFormatNegativeFormat.MINUS_RIGHT:
            return `${magnitude}-`;
        case NumberFormatNegativeFormat.PARENTHESES:
        default:
            return `(${magnitude})`;
    }
};

interface ScaledMagnitude {
    isNegative: boolean;
    isRoundedZero: boolean;
    magnitudeWithSymbol: string;
}

// The magnitude machinery only needs these four controls. Percent/ratio cells
// pass a derived format whose decimal count (`percentDecimalPlaces`) is a plain
// number, so `decimalPlaces` is widened from the amount union here.
type ScaledMagnitudeFormat = Pick<
    NumberFormatValues,
    "hasThousandsSeparator" | "amountScale" | "currencySymbol"
> & { decimalPlaces: number };

// Cached to avoid constructing a new Intl.NumberFormat on every cell render.
const scaledMagnitudeFormatterCache = new Map<string, Intl.NumberFormat>();

const getScaledMagnitudeFormatter = (
    numberFormat: ScaledMagnitudeFormat,
): Intl.NumberFormat => {
    const cacheKey = `${numberFormat.decimalPlaces}-${numberFormat.hasThousandsSeparator}`;
    const cachedFormatter = scaledMagnitudeFormatterCache.get(cacheKey);
    if (cachedFormatter) {
        return cachedFormatter;
    }

    const formatter = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: numberFormat.decimalPlaces,
        maximumFractionDigits: numberFormat.decimalPlaces,
        useGrouping: numberFormat.hasThousandsSeparator,
    });
    scaledMagnitudeFormatterCache.set(cacheKey, formatter);
    return formatter;
};

const formatScaledMagnitude = (
    value: number,
    numberFormat: ScaledMagnitudeFormat,
): ScaledMagnitude => {
    const scaledMagnitude = Math.abs(
        scaleAmount(value, numberFormat.amountScale),
    );
    const formatter = getScaledMagnitudeFormatter(numberFormat);
    const magnitudeParts = formatter.formatToParts(scaledMagnitude);
    const isRoundedZero = magnitudeParts.every(
        (part) =>
            (part.type !== "integer" && part.type !== "fraction") ||
            !/[1-9]/.test(part.value),
    );
    return {
        isNegative: value < 0 && !isRoundedZero,
        isRoundedZero,
        magnitudeWithSymbol: `${currencySymbolText(numberFormat.currencySymbol)}${magnitudeParts.map((part) => part.value).join("")}`,
    };
};

const deriveAmountMagnitudeFormat = (
    numberFormat: NumberFormatValues,
): ScaledMagnitudeFormat => ({
    decimalPlaces: numberFormat.decimalPlaces,
    hasThousandsSeparator: numberFormat.hasThousandsSeparator,
    amountScale: numberFormat.amountScale,
    currencySymbol:
        numberFormat.currencyPlacement ===
        NumberFormatCurrencyPlacement.COLUMN_HEADERS
            ? NumberFormatCurrencySymbol.NONE
            : numberFormat.currencySymbol,
});

export const formatMoneyColumnHeader = (
    header: string,
    numberFormat?: NumberFormatValues,
): string => {
    if (
        numberFormat?.currencyPlacement !==
        NumberFormatCurrencyPlacement.COLUMN_HEADERS
    ) {
        return header;
    }
    const symbol = currencySymbolText(numberFormat.currencySymbol).trim();
    return symbol ? `${header} (${symbol})` : header;
};

export interface FormattedAmount {
    isNegative: boolean;
    text: string;
}

// Shared zero rendering: a rounded-to-zero magnitude is never negative, so red
// never follows it. BLANK hides the cell, DASH shows the placeholder, ZERO
// paints the numeric magnitude (e.g. `0.00`, `0.00%`).
const applyZeroDisplay = (
    zeroDisplay: NumberFormatZeroDisplay,
    magnitude: string,
    emptyValuePlaceholder: string,
): FormattedAmount => {
    switch (zeroDisplay) {
        case NumberFormatZeroDisplay.BLANK:
            return { isNegative: false, text: "" };
        case NumberFormatZeroDisplay.ZERO:
            return { isNegative: false, text: magnitude };
        case NumberFormatZeroDisplay.DASH:
        default:
            return { isNegative: false, text: emptyValuePlaceholder };
    }
};

export interface FormatAmountWithNumberFormatResultParams {
    emptyValuePlaceholder?: string;
    numberFormat: NumberFormatValues;
    value: number;
}

export const formatAmountWithNumberFormatResult = ({
    emptyValuePlaceholder = "-",
    numberFormat,
    value,
}: FormatAmountWithNumberFormatResultParams): FormattedAmount => {
    if (!Number.isFinite(value)) {
        return { isNegative: false, text: emptyValuePlaceholder };
    }

    const { isNegative, isRoundedZero, magnitudeWithSymbol } =
        formatScaledMagnitude(value, deriveAmountMagnitudeFormat(numberFormat));

    // Checked against the rounded magnitude so zeroDisplay also applies to
    // values that only round to zero at the configured decimals.
    if (isRoundedZero) {
        return applyZeroDisplay(
            numberFormat.zeroDisplay,
            magnitudeWithSymbol,
            emptyValuePlaceholder,
        );
    }

    return {
        isNegative,
        text: isNegative
            ? applyNegativeFormat(
                  magnitudeWithSymbol,
                  numberFormat.negativeFormat,
              )
            : magnitudeWithSymbol,
    };
};

export const formatAmountWithNumberFormat = (
    value: number,
    numberFormat: NumberFormatValues,
    emptyValuePlaceholder = "-",
): string =>
    formatAmountWithNumberFormatResult({
        emptyValuePlaceholder,
        numberFormat,
        value,
    }).text;

export interface AccountingNumberFormatterOptions {
    numberFormat?: NumberFormatValues;
}

export const accountingNumberFormatter = (
    value: number,
    emptyValuePlaceholder = "-",
    options?: AccountingNumberFormatterOptions,
) => {
    if (options?.numberFormat) {
        return formatAmountWithNumberFormat(
            value,
            options.numberFormat,
            emptyValuePlaceholder,
        );
    }

    if (!value) {
        return emptyValuePlaceholder;
    }

    const output = currencyFormatterFinancialStatements.format(value, {
        includeFractions: true,
    });

    return value < 0 ? `(${output})` : output;
};

export const differenceAbsoluteFormatterResult = (
    value: number | null,
    emptyValuePlaceholder = "-",
    options?: AccountingNumberFormatterOptions,
): FormattedAmount => {
    if (value === null || !Number.isFinite(value)) {
        return { isNegative: false, text: emptyValuePlaceholder };
    }

    // Amount magnitude (decimals, currency, scale, grouping) with a signed
    // positive (`+`) and the saved negative style / zero display, matching
    // Excel's absolute-difference pattern.
    if (options?.numberFormat) {
        const numberFormat = options.numberFormat;
        const { isNegative, isRoundedZero, magnitudeWithSymbol } =
            formatScaledMagnitude(
                value,
                deriveAmountMagnitudeFormat(numberFormat),
            );
        if (isRoundedZero) {
            return applyZeroDisplay(
                numberFormat.zeroDisplay,
                magnitudeWithSymbol,
                emptyValuePlaceholder,
            );
        }
        return {
            isNegative,
            text: isNegative
                ? applyNegativeFormat(
                      magnitudeWithSymbol,
                      numberFormat.negativeFormat,
                  )
                : `+${magnitudeWithSymbol}`,
        };
    }

    if (value === 0) {
        return { isNegative: false, text: emptyValuePlaceholder };
    }

    const output = currencyFormatterFinancialStatements.format(
        Math.abs(value),
        { includeFractions: true },
    );

    return {
        isNegative: value < 0,
        text: value > 0 ? `+${output}` : `-${output}`,
    };
};

// Percentage and ratio cells reuse the amount magnitude machinery on a derived
// format: percent decimals, no scaling, no currency. Grouping, negative style,
// zero display and red still come from the saved format.
const derivePercentMagnitudeFormat = (
    numberFormat: NumberFormatValues,
): ScaledMagnitudeFormat => ({
    decimalPlaces: numberFormat.percentDecimalPlaces,
    hasThousandsSeparator: numberFormat.hasThousandsSeparator,
    amountScale: NumberFormatAmountScale.FULL,
    currencySymbol: NumberFormatCurrencySymbol.NONE,
});

export interface FormatPercentWithNumberFormatResultParams {
    emptyValuePlaceholder?: string;
    numberFormat: NumberFormatValues;
    value: number;
}

export const formatPercentWithNumberFormatResult = ({
    emptyValuePlaceholder = "-",
    numberFormat,
    value,
}: FormatPercentWithNumberFormatResultParams): FormattedAmount => {
    if (!Number.isFinite(value)) {
        return { isNegative: false, text: emptyValuePlaceholder };
    }

    const { isNegative, isRoundedZero, magnitudeWithSymbol } =
        formatScaledMagnitude(
            value,
            derivePercentMagnitudeFormat(numberFormat),
        );
    const magnitudeWithPercent = `${magnitudeWithSymbol}%`;

    // Dash and blank zeros carry no `%`; a numeric zero keeps it (`0.00%`).
    if (isRoundedZero) {
        return applyZeroDisplay(
            numberFormat.zeroDisplay,
            magnitudeWithPercent,
            emptyValuePlaceholder,
        );
    }

    return {
        isNegative,
        text: isNegative
            ? applyNegativeFormat(
                  magnitudeWithPercent,
                  numberFormat.negativeFormat,
              )
            : magnitudeWithPercent,
    };
};

export const differenceAbsoluteFormatter = (
    value: number | null,
    emptyValuePlaceholder = "-",
    options?: AccountingNumberFormatterOptions,
): string =>
    differenceAbsoluteFormatterResult(value, emptyValuePlaceholder, options)
        .text;

export const differencePercentageFormatter = (
    value: number | null,
    emptyValuePlaceholder = "-",
    decimalPlaces = 2,
) => {
    if (value === null || value === 0) {
        return emptyValuePlaceholder;
    }

    const formatted = formatPercentage(Math.abs(value), decimalPlaces);
    return value > 0 ? `+${formatted}%` : `-${formatted}%`;
};

export const formatPercentage = (
    value: number,
    decimalPlaces: number,
): string =>
    value.toLocaleString("en-US", {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    });

export const ratioPercentageFormatter = (
    value: number | null,
    emptyValuePlaceholder = "\u2013",
    decimalPlaces = 1,
): string => {
    if (value === null || value === 0) {
        return emptyValuePlaceholder;
    }

    return `${formatPercentage(value, decimalPlaces)}%`;
};
